import { IoPeopleCircleOutline } from 'react-icons/io5'

import { IResourceConfig } from '@/Core/Contracts/UI'
import { copyableListView, renderDateTimeView } from '@/Ship/Helpers/fieldViews'
import { formatPhone } from '@/Ship/Helpers/formatPhone'
import { MIN_PASSWORD_LENGTH, PASSWORD_TEXTS } from '@/Ship/Helpers/passwordPolicy'

import { ACCESS_FIELDS, MANAGED_FIELDS } from '../Contracts/IUser'
import { USER_BASE_ROLE, USER_ROLE_OPTIONS, USER_STATUS_OPTIONS, assignedRoles } from '../Contracts/UserOptions'
import { UserEditTemplate } from '../Templates/UserEditTemplate'

import type { UserCardValues } from '../Contracts/IUser'

interface FieldLike {
    formContext?: { values?: Record<string, unknown> }
    mergeInputProps?: (props: Record<string, unknown>) => void
    setIsRequired?: (required: boolean) => void
}

interface DepFnArgs {
    field: FieldLike
    formContext: { contextKey?: string; values?: Record<string, unknown> }
}

const isNewRecord = (values: Record<string, unknown> | undefined): boolean => {
    const uuid = values?.['uuid']

    return typeof uuid !== 'string' || uuid === ''
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * Телефон-логін, телефони, відділи й сортування змінює лише супер
 * адміністратор (ВП-5): у легасі ці поля бачили тільки в картці
 * адміністратора, а не в профілі. Бек дає на це окреме право картки
 * `update_managed` і без нього відхиляє зміну.
 *
 * ⚠️ Право саме картки, а не загальне: загальні права фронт бере лише під час
 * входу. Нова картка прав ще не має — її відкриває лише той, хто заводить
 * співробітників, тож там нічого не блокуємо.
 *
 * Повертає `undefined`, а не `false`: `false` з `depFn` ядро читає як
 * «сховати поле».
 */
const lockUnlessManager = ({ field, formContext }: DepFnArgs): void => {
    if (formContext.contextKey === 'filterFormContext') return

    const values = formContext.values ?? {}
    const permissions = values['_permissions'] as UserCardValues['_permissions']

    // Обидва напрями: depFn перевикликається на кожну зміну значень форми
    field.mergeInputProps?.({ disabled: !isNewRecord(values) && permissions?.update_managed !== true })
}

/** Пароль задається лише у формі створення — там він обов'язковий (Q6). */
const requiredOnCreate = ({ field, formContext }: DepFnArgs): void => {
    field.setIsRequired?.(isNewRecord(formContext.values))
}

/**
 * Пароль нового співробітника. Ядро перевіряє обов'язковість лише за
 * `isRequired`, а той ми вмикаємо з `depFn` — тож порожнє значення ловимо й
 * тут: валідатори ядро кличе для всіх полів ресурсу, навіть не намальованих.
 */
const passwordValidator = (value: unknown, field?: FieldLike): string | null => {
    if (!isNewRecord(field?.formContext?.values)) return null

    const password = text(value)

    if (password === '') return 'Поле "Пароль" є обов\'язковим для заповнення'

    return password.length < MIN_PASSWORD_LENGTH ? PASSWORD_TEXTS.tooShort : null
}

/** Підтвердження: розбіжність ловимо до запиту — бек його не бачить (D-28). */
const confirmationValidator = (value: unknown, field?: FieldLike): string | null => {
    if (!isNewRecord(field?.formContext?.values)) return null

    const confirmation = text(value)

    if (confirmation === '') return 'Поле "Повторити пароль" є обов\'язковим для заповнення'

    return confirmation !== text(field?.formContext?.values?.['plainPassword']) ? PASSWORD_TEXTS.mismatch : null
}

/**
 * Тіло запиту картки перед відправкою.
 *
 * Ядро шле в PATCH усі значення картки, а бек перевіряє права на поля тіла
 * ще до десеріалізації (`SPEC-005` §3.1). Поля, які людина змінити не може
 * (вони заблоковані у формі), прибираємо: вони однаково не змінилися, а
 * зайвий ключ у тілі не повинен важити, чи збережеться профіль.
 *
 * У створенні: підтвердження пароля бекові не потрібне, а без жодної ролі
 * людина — «Співробітник» (`ROLE_USER`).
 */
const prepareUserPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: UserCardValues }
}): void => {
    const current = formContext?.values ?? {}
    const isNew = isNewRecord(current)

    delete values['plainPasswordConfirm']
    // Пароль Google Workspace пишеться лише своєю вкладкою
    delete values['gmailAppPassword']

    if (!isNew || text(values['plainPassword']) === '') delete values['plainPassword']

    if (isNew) {
        const roles = assignedRoles(values['roles'])

        values['roles'] = roles.length > 0 ? roles : [USER_BASE_ROLE.value]

        return
    }

    const permissions = current._permissions ?? {}

    if (permissions.update_access !== true) ACCESS_FIELDS.forEach((name) => delete values[name])
    if (permissions.update_managed !== true) MANAGED_FIELDS.forEach((name) => delete values[name])
}

/**
 * Співробітники (`SPEC-005` §4, §7). Список — на двох сторінках:
 * «Активні співробітники» (`UserPage`, `/users`) і «Деактивовані
 * співробітники» (`UserInactivePage`, `/users/inactive`); `pageUrl` ресурсу
 * лишається для адрес карток `/users/{uuid}`.
 *
 * Підписи «Ім'я» й «По батькові» — виправлені (ВП-9 (а)); `accessProperties`,
 * `externalCode`, `dateOfBirth`, `uuid`, `shortName` у формах не показуються.
 */
export const UserResource: IResourceConfig = {
    title: 'Співробітники',
    fields: {
        lastName: {
            label: 'Прізвище',
            isRequired: true,
        },
        firstName: {
            label: "Ім'я",
            isRequired: true,
        },
        middleName: {
            label: 'По батькові',
        },
        position: {
            label: 'Посада',
        },
        departments: {
            label: 'Відділи компанії',
            type: 'select',
            isMultiple: true,
            isRequired: true,
            resource: 'Setting.Department',
            depFn: lockUnlessManager,
        },
        sorting: {
            label: 'Сортування',
            type: 'number',
            depFn: lockUnlessManager,
        },
        phone: {
            label: 'Мобільний телефон',
            type: 'phone',
            isRequired: true,
            view: copyableListView(formatPhone),
            depFn: lockUnlessManager,
        },
        phones: {
            label: 'Телефони',
            isMultiple: true,
            type: 'phone',
            depFn: lockUnlessManager,
        },
        email: {
            label: 'E-mail',
            isRequired: true,
            // Пошту частіше переносять, ніж читають — у перегляді копіюється
            view: copyableListView(),
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: USER_STATUS_OPTIONS,
            getChangeUrl: ({ item }: { item: { uuid: string } }) => `/services/users/${item.uuid}/change_status`,
            depFn: ({ field, formContext }: DepFnArgs) => {
                if (formContext.contextKey !== 'filterFormContext') {
                    field.mergeInputProps?.({
                        disabled: true,
                    })
                }
            },
        },
        roles: {
            label: 'Ролі',
            isMultiple: true,
            type: 'select',
            options: [...USER_ROLE_OPTIONS, USER_BASE_ROLE],
        },
        photo: {
            label: 'Фото',
            type: 'file',
            uploadUrl: '/services/user/photo_files',
        },
        plainPassword: {
            label: 'Пароль',
            type: 'password',
            inputProps: { autoComplete: 'new-password' },
            depFn: requiredOnCreate,
            validators: [passwordValidator],
        },
        plainPasswordConfirm: {
            label: 'Повторити пароль',
            type: 'password',
            inputProps: { autoComplete: 'new-password' },
            depFn: requiredOnCreate,
            validators: [confirmationValidator],
        },
        lastActivityAt: {
            label: 'Остання активність',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            view: renderDateTimeView,
        },
        createdAt: {
            label: 'Дата створення',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            view: renderDateTimeView,
        },
        updatedAt: {
            label: 'Дата зміни',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            view: renderDateTimeView,
        },
    },
    drawer: {
        // Два стовпці блоків картки починаються з 960 px
        width: 1100,
    },
    // Колонки й фільтри легасі (`UserCrud.jsx:23-28`) + мобільний телефон,
    // задуманий там, але не показаний (`SPEC-005` §7.1)
    table: {
        showColumns: ['firstName', 'lastName', 'position', 'email', 'phone'],
    },
    filter: {
        includeFields: ['firstName', 'lastName', 'position', 'email', 'phone'],
    },
    form: {
        template: UserEditTemplate,
        eventHandlers: {
            onBeforeSendSaveRequest: {
                UserPayload: prepareUserPayload,
            },
        },
    },
    restUrl: '/services/users',
    pageUrl: '/users',
    pageConfig: {
        title: 'Співробітники',
        icon: <IoPeopleCircleOutline />,
    },
    settings: {
        searchFields: ['firstName', 'lastName', 'middleName', 'email', 'phone', 'position'],
        AddDrawerModeCrudFeature: {
            enableMenuDeleteItem: false,
        },
    },
}
